import { ExamApi, examsApi } from "api/examsApi";
import { useExams } from "api/hooks/useExams";
import { AdminResourceDeleteModal, PageHeader, PageMain } from "components/molecules";
import { routes } from "consts";
import React, { useEffect } from "react";
import { FaCheck, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useDeleteResourceActions } from "utils/store";
import { useDuplicateResourceActions } from "utils/store/duplicate";
import {
  TanTable,
  TanTableColumn,
  useFilters,
  useInitFilters,
  usePagination,
  useSorting,
} from "components/molecules";

const FILTER_NAME = "exams-filter";
export const ExamsPage = () => {
  const { getInitFilterValue } = useInitFilters(FILTER_NAME);

  return (
    <ExamsPageInner
      initValues={{
        id: getInitFilterValue(COLUMN_KEYS.ID, "like"),
        name: getInitFilterValue(COLUMN_KEYS.NAME, "like") ?? "",
      }}
    />
  );
};
interface ExamsPageInnerProps {
  initValues: {
    id?: string;
    name: string;
  };
}
const ExamsPageInner = ({ initValues }: ExamsPageInnerProps) => {
  const { setDeleteResource } = useDeleteResourceActions();
  const { updateAllFilters, filter } = useFilters(FILTER_NAME);
  const { setDuplicateResource } = useDuplicateResourceActions();
  const { limit, onPaginationChange, skip, pagination } = usePagination();
  const { sorting, onSortingChange, fieldWithOrder } = useSorting();

  const { isFetching, isLoading, invalidate, exams, total } = useExams(skip, limit, filter, fieldWithOrder);

  useEffect(() => {
    invalidate();
  }, [sorting, pagination, filter]);

  const navigate = useNavigate();

  const columns = React.useMemo<TanTableColumn<ExamApi>[]>(() => cols, []);
  return (
    <PageMain>
      <PageHeader
        button={{
          title: "Dodaj +",
          onClick: () => navigate(routes.EXAMS_ADD),
        }}
      >
        Egzaminy
      </PageHeader>
      <TanTable
        data={exams}
        columns={columns}
        total={total}
        loading={isLoading}
        fetching={isFetching}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        onDelete={(x) => setDeleteResource(x.id)}
        onEdit={(x) => navigate(routes.EXAMS_EDIT(x.id))}
        initialFilters={[
          {
            id: COLUMN_KEYS.ID,
            value: initValues.id,
          },
          {
            id: COLUMN_KEYS.NAME,
            value: initValues.name,
          },
        ]}
        onDuplicate={(x) =>
          setDuplicateResource({
            resource: { id: x.id, name: x.name },
            duplicateAction: async () => {
              const resp = await examsApi.duplicate(x.id);
              return resp.data;
            },
            onSuccess: (newId: number) => {
              invalidate();
              navigate(routes.EXAMS_CREATOR(newId));
            },
          })
        }
        onClearFilters={() => updateAllFilters([])}
        onFiltersChange={(filters) => {
          updateAllFilters(filters.map((x) => ({ key: x.id, value: x.apiFilter })));
        }}
        pagination={pagination}
        sorting={sorting}
        onRowClick={(x) => navigate(routes.EXAMS_CREATOR(x.id))}
      />
      <AdminResourceDeleteModal
        title="Usuń egzamin"
        description="Jesteś pewien że chcesz usunąć egzamin?"
        onSuccessDelete={() => invalidate()}
        deleteAction={examsApi.delete}
      />
    </PageMain>
  );
};
const COLUMN_KEYS = {
  ID: "id",
  NAME: "name",
};
const cols: TanTableColumn<ExamApi>[] = [
  {
    id: "id",
    accessorKey: "id",
    header: "ID",
    enableColumnFilter: true,
    size: 70,
    cell: (info) => info.getValue(),
    meta: {
      filterVariant: "id",
    },
  },
  {
    id: "name",
    accessorKey: "name",
    header: "Nazwa",
    size: 500,
    enableColumnFilter: true,
  },
  {
    id: "isSystem",
    accessorKey: "isSystem",
    header: "Egzamin systemowy",
    enableColumnFilter: false,
    cell: (info) =>
      info.getValue() ? <FaCheck className="text-emerald-500" /> : <FaTimes className="text-slate-500" />,
  },
];
